import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { useDispatch } from 'react-redux';

const InvitationContainer = styled.div`
  display: flex;
  height: 100vh;
  background-color: ${({ theme }) => theme.colors.background.main};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
  }
`;

const LogoSide = styled.div`
  flex: 0 0 35%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.background.sidebar};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex: 0;
    padding: 60px 0 30px;
    background-color: transparent;
  }
`;

const Logo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  img {
    height: 60px;
    width: auto;
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const LogoText = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 18px;
  font-weight: 500;
  margin-top: 8px;
`;

const ContentSide = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 1rem;
  }
`;

const InvitationCard = styled.div`
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 100%;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.text.primary};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.75rem;
    margin-bottom: 1rem;
  }
`;

const InvitationSection = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionLabel = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 0.5rem;
`;

const HandleInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 1rem;
  margin-bottom: 0.5rem;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px #9333EA;
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.text.disabled};
  }
`;

const InputWithIcon = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
`;

const IconContainer = styled.div`
  position: absolute;
  right: 12px;
  display: flex;
  gap: 8px;
`;

const Icon = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 4px;
`;

const ReferralInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 1rem;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px #9333EA;
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.text.disabled};
  }
`;

const ErrorMessage = styled.div`
  color: #EF4444;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  text-align: left;
`;

const Button = styled.button`
  width: 100%;
  padding: 0.75rem;
  background-color: #9333EA;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #7928CA;
  }
  
  &:disabled {
    background-color: #4B5563;
    cursor: not-allowed;
  }
`;

const SkipLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.875rem;
  margin-top: 1rem;
  cursor: pointer;
  text-align: center;
  width: 100%;
  padding: 0.5rem;
  
  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Invitation: React.FC = () => {
  const [handle, setHandle] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState('');
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    
    // Basic validation - handle must start with @
    if (handle && !handle.startsWith('@')) {
      setFormError('Handle must start with @');
      return;
    }
    
    // Validate referral code format if provided
    if (referralCode && !/^[A-Za-z0-9]{6,10}$/.test(referralCode)) {
      setFormError('Invalid referral code format');
      return;
    }
    
    setIsLoading(true);
    
    // TODO: Implement invitation submission logic
    
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      navigate('/accept-invitation', {
        state: { 
          phoneNumber: location.state?.phoneNumber,
          handle,
          referralCode
        }
      });
    }, 1000);
  };
  
  const handleSkip = () => {
    navigate('/accept-invitation', {
      state: { phoneNumber: location.state?.phoneNumber }
    });
  };
  
  return (
    <InvitationContainer>
      <LogoSide>
        <Logo>
          <img src="/creator-logo.svg" alt="BlinkLink Creator" />
       
        </Logo>
      </LogoSide>
      <ContentSide>
        <InvitationCard>
          <Title>Have an invitation?</Title>
          
          <form onSubmit={handleSubmit}>
            <InvitationSection>
              <SectionLabel>Pre-approved by a Enterprise?</SectionLabel>
              <InputWithIcon>
                <HandleInput
                  type="text"
                  placeholder="@myhandle"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                />
                <IconContainer>
                  <Icon src="/icons/enterprise-orange.svg" alt="Enterprise" />
                  <Icon src="/icons/enterprise-blue.svg" alt="Enterprise" />
                </IconContainer>
              </InputWithIcon>
            </InvitationSection>
            
            <InvitationSection>
              <SectionLabel>Referred by a creator?</SectionLabel>
              <InputWithIcon>
                <ReferralInput
                  type="text"
                  placeholder="Enter referral code to earn more"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value)}
                />
                <IconContainer>
                  <Icon src="/icons/creator-pink.svg" alt="Creator" />
                  <Icon src="/icons/creator-green.svg" alt="Creator" />
                </IconContainer>
              </InputWithIcon>
            </InvitationSection>
            
            {formError && <ErrorMessage>{formError}</ErrorMessage>}
            
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Processing...' : 'Next'}
            </Button>
            
            <SkipLink type="button" onClick={handleSkip}>
              Continue without accepting
            </SkipLink>
          </form>
        </InvitationCard>
      </ContentSide>
    </InvitationContainer>
  );
};

export default Invitation;
